import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Users } from 'lucide-react'
import { teams as api, users as userApi, vms as vmApi } from '@/api/endpoints'
import type { Team, TeamMember, User } from '@/api/types'
import { Err } from '@/components/Err'
import { InlineEdit } from '@/components/InlineEdit'
import { ToneBadge } from '@/components/ToneBadge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Crumb, Empty, PageHead, Section } from '@/components/layout/Page'
import { vmToneOf, vmWord } from '@/domain/lease'
import { ownerLabel } from '@/domain/owner'
import { splitTags } from '@/domain/team'
import { teamRoleWord } from '@/domain/words'
import { useFallback, useRefetchOn } from '@/hooks/useEventStream'
import { peopleChanged } from '@/hooks/usePage'
import { usePoll } from '@/hooks/usePoll'
import { useSession } from '@/hooks/useSession'
import { useRefreshAfterWrites, useWorldRefresh } from '@/hooks/useWorld'
import { ACTION, ACTION_DANGER, CHIP } from './vm/chips'
import { byTeamRole } from './memberRows'
import { PeopleTable } from './PeopleTable'
import { TeamDeleteForm } from './TeamForms'
import { TeamMembers } from './TeamMembers'
import { opensDetail, personRow } from './model'
import type { World } from './model'
import type { Candidate } from './user/teamPick'

// VM 표와 멤버의 읽기. 스트림이 붙어 있으면 알림이 메우므로 느긋하게 읽는다(`useFallback`).
const POLL_MS = 8000
// 쓰기 하나가 프레임 여럿을 낸다 — 잠깐 모아 한 번 읽는다.
const DEBOUNCE_MS = 1000

// 팀 상세 — 팀 목록의 줄을 누르면 온다. 누가 들어 있고 누가 관리하는가(멤버), 그 사람들이 지금 쓰는 VM, 그 팀이 무엇을 위한 것인가(태그).
// 팀은 이미 읽은 `World` 의 팀 목록에서 찾는다.
// **멤버 줄의 이름·이메일·직위는 서버가 그 팀으로 좁힌 계정 목록이다**(`users.list({ team })`) — 팀의 멤버 응답은 아이디와 역할만 싣는다.
// 모든 사람을 받아 잇지 않는다.
// **VM 표는 서버가 그 팀으로 좁힌 VM 목록이다**(`vms.list({ team, live: true })`) — 팀 줄의 `vm_count` 가 센 집합이라
// 목록의 수와 표의 줄이 어긋나지 않는다. 표를 읽기 전의 제목 줄은 그 수를 쓴다.
// **조작은 관리하는 사람(소유자·최고 관리자)에게만 선다** — `멤버 수정`·`삭제` 는 제목 줄의 칩이고, 태그는 그 자리에서 고친다(`InlineEdit`).
// 멤버 줄에는 버튼이 없다 — 넣고 빼기와 관리자 지정·해제는 전부 `멤버 수정` 의 창(`TeamMembers`)이다.
// 팀은 쿼터를 갖지 않는다 — 팀 쿼터 대비 사용량도 여기 없다.

type Open = 'members' | 'delete' | null

// 팀에서 팀으로 옮기면 경로만 바뀌고 화면은 그대로 남는다 — 따로 읽은 VM 표가 새 답이 올 때까지 앞 팀의 줄과 수로
// 서지 않게 팀마다 새로 그린다.
export default function TeamView({ world }: { world: World }) {
  const { id } = useParams()
  return <TeamDetail key={id} id={id} world={world} />
}

function TeamDetail({ id, world }: { id: string | undefined; world: World }) {
  const me = useSession()
  const nav = useNavigate()
  const refreshWorld = useWorldRefresh()
  const [open, setOpen] = useState<Open>(null)
  const team = world.teams.find((t) => t.id === id)
  const found = team !== undefined
  const table = usePoll(
    () => (id && found ? vmApi.list({ team: id, live: true }) : Promise.resolve(null)),
    useFallback(POLL_MS),
    [id, found],
  )
  // 표의 줄은 VM 을 만들고 지우는 쓰기(`vm`·`lease`)와 멤버를 넣고 빼는 쓰기(`team`)로, 줄의 사용자 이름은 이름을 바꾸는 쓰기로 낡는다.
  useRefetchOn(
    (c) => c.kind === 'vm' || c.kind === 'lease' || c.kind === 'team' || (c.kind === 'user' && c.what === 'profile'),
    table.refresh,
    DEBOUNCE_MS,
  )
  // 관리자(제한)는 알림을 받지 않는다 — 멤버 수정 같은 쓰기 뒤의 다시 읽기(`useWorldRefresh`)에 표와 멤버를 단다.
  useRefreshAfterWrites(table.refresh)
  // 멤버 줄의 계정은 계정의 쓰기(이름·직위)와 멤버를 넣고 빼는 쓰기로 낡는다.
  const members = usePoll(() => (id && found ? userApi.list({ team: id }) : Promise.resolve(null)), useFallback(POLL_MS), [id, found])
  useRefetchOn(peopleChanged, members.refresh, DEBOUNCE_MS)
  useRefreshAfterWrites(members.refresh)

  if (!team) return <Empty>없는 팀</Empty>

  const manages = me.role === 'superadmin' || team.my_role === 'owner'
  const vms = table.data
  const vmCount = vms?.length ?? team.vm_count
  const tags = team.tags ?? []
  // 멤버의 줄 — 이름·이메일·직위는 그 팀으로 좁혀 읽은 계정에서 잇고, 그 읽기에 없는 멤버(그 사이 들어온 사람)는
  // 아이디만으로 선다. 순서는 관리자가 먼저다(`byTeamRole`). 멤버 창은 이 줄들이 골라진 채 열린다 —
  // 그 읽기가 온 뒤에 선다. 먼저 열면 창이 아이디만 쥔 줄을 고른 채라 창이 열린 동안 이름이 비어 있다.
  const byId = new Map((members.data ?? []).map((u) => [u.id, u]))
  const roster = [...team.members].sort(byTeamRole).map((m) => personRow(byId.get(m.user_id) ?? stranger(m), world.teams))
  const close = () => setOpen(null)

  return (
    <>
      <Crumb steps={[{ label: '팀', to: '/teams' }, { label: team.display_name }]} />
      <PageHead
        icon={<Users className="size-5" />}
        kind="팀"
        title={team.display_name}
        afterTitle={
          <>
            {team.my_role === 'owner' ? <ToneBadge tone="ok">관리</ToneBadge> : null}
            {!team.enabled ? <ToneBadge tone="warn">비활성</ToneBadge> : null}
          </>
        }
        facts={vmCount === undefined ? `멤버 ${team.members.length}명` : `멤버 ${team.members.length}명 · VM ${vmCount}대`}
        right={
          manages ? (
            <div className="mt-0.5 flex flex-wrap items-center justify-end gap-1.5">
              <button type="button" className={CHIP} onClick={() => setOpen('members')}>
                멤버 수정
              </button>
              <button type="button" className={`${ACTION} ${ACTION_DANGER}`} onClick={() => setOpen('delete')}>
                삭제
              </button>
            </div>
          ) : undefined
        }
      />

      <Section title={`멤버 ${team.members.length}`}>
        <Err error={members.error} />
        {team.members.length === 0 ? (
          <Empty>멤버 없음</Empty>
        ) : members.data ? (
          <TeamPeople team={team} people={roster} world={world} />
        ) : members.loading ? (
          <Empty>불러오는 중</Empty>
        ) : null}
      </Section>

      <Section title={vmCount === undefined ? 'VM' : `VM ${vmCount}`}>
        <Err error={table.error} />
        {vms === null ? (
          table.loading ? (
            <Empty>불러오는 중</Empty>
          ) : null
        ) : vms.length === 0 ? (
          <Empty>VM 없음</Empty>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>VM</TableHead>
                  <TableHead>사용자</TableHead>
                  <TableHead className="w-28">상태</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vms.map((v) => (
                  <TableRow key={v.id} onClick={() => nav(`/vms/${v.lease_id}`)} className="cursor-pointer hover:bg-surface-2">
                    <TableCell className="font-medium max-sm:basis-full">{v.name}</TableCell>
                    <TableCell>{ownerLabel(v.owner)}</TableCell>
                    <TableCell>
                      <ToneBadge tone={vmToneOf(v.state)}>{vmWord(v.state)}</ToneBadge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      <Section title="태그">
        <InlineEdit
          label="태그"
          value={tags.join(', ')}
          editable={manages}
          placeholder="수업, 2026-1학기"
          display={
            tags.length === 0 ? (
              <span className="text-[13px] text-muted-foreground">태그 없음</span>
            ) : (
              <span className="flex flex-wrap items-center gap-1.5">
                {tags.map((x) => (
                  <span key={x} className={CHIP}>
                    {x}
                  </span>
                ))}
              </span>
            )
          }
          onSave={async (v) => {
            await api.patch(team.id, { tags: splitTags(v) })
            refreshWorld()
          }}
        />
      </Section>

      <Dialog open={open !== null} onOpenChange={(v) => (v ? undefined : close())}>
        <DialogContent aria-describedby={undefined} className={open === 'members' ? 'sm:max-w-3xl' : undefined}>
          {open === 'members' ? (
            members.data ? (
              <TeamMembers
                team={team}
                seed={roster}
                teams={world.teams}
                onClose={close}
                onDone={() => {
                  close()
                  // 넣고 뺀 사람과 그 사람의 VM 이 표에 들고 난다 — 표와 멤버는 이 다시 읽기에 달려 있다.
                  refreshWorld()
                }}
              />
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>{team.display_name} 멤버</DialogTitle>
                </DialogHeader>
                <Err error={members.error} />
                {members.loading ? <Empty>불러오는 중</Empty> : null}
              </>
            )
          ) : null}
          {open === 'delete' ? <TeamDeleteForm teams={[team]} onClose={close} onDone={() => nav('/teams')} /> : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

/** 멤버의 표 — 줄은 부르는 쪽이 이은 것(`people`)이고 역할 칸은 팀의 멤버 응답의 것이다. */
function TeamPeople({ team, people, world }: { team: Team; people: Candidate[]; world: World }) {
  const me = useSession()
  const roles = new Map(team.members.map((m) => [m.user_id, m.role]))
  return (
    <PeopleTable
      group={people}
      last={{ head: '역할', cell: (p) => teamRoleWord(roles.get(p.user.id) ?? '') }}
      opens={(p) => opensDetail(world, me, p.user.id)}
    />
  )
}

const stranger = (m: TeamMember): User => ({ id: m.user_id, username: m.username, role: 'user' })
